import BulkAffiliateEmailModal from './BulkAffiliateEmailModal';
import useAffiliateBulkEmailStore, { type RecipientData } from '@/stores/affiliate-bulk-email-store';
import { useCallback, useEffect, useRef } from 'react';

/**
 * What any "write to this recipient" gesture hands the shared composer: who
 * to write to, which programme (if any) is locked, and the draft the calling
 * surface seeded it with.
 */
export interface SeededEmailComposerRequest {
  /**
   * Remount key: a new request gets a new composer, so a draft the brand
   * started typing for one gift or card never lands in the body for another.
   */
  seedKey: string;
  /** null while the caller is still resolving the recipient's address. */
  recipient: RecipientData | null;
  programId: string | null;
  draft: { subject: string; body: string } | null;
}

/**
 * Where any "write to this recipient" gesture actually opens.
 *
 * The composer is the one the brand already uses from the affiliate table,
 * not a copy of it: same validation, same sending rail, same "connect a
 * mailbox" screen when there is none. All this host does is put one
 * recipient and one programme in front of it, and hand over the draft the
 * calling surface seeded it with.
 *
 * It is meant to live at page/shell level, sibling to whatever panel or
 * drawer triggered the request: a modal owned by a component that unmounts
 * would close with it.
 */
export const SeededEmailComposerHost = (props: {
  request: SeededEmailComposerRequest | null;
  onClose: () => void;
}) => {
  const { request, onClose: onCloseProp } = props;
  const seededKey = useRef<string | null>(null);

  // The effect watches the three values it actually writes, never the request
  // object: callers build that object inline, so its identity changes on every
  // render of their own and the store would be rewritten each time.
  const seedKey = request?.seedKey ?? null;
  const recipient = request?.recipient ?? null;
  const programId = request?.programId ?? null;

  // The composer reads its recipients from the shared selection store, so the
  // caller's single recipient is written there and taken back on close:
  // leaving it behind would preselect that person the next time the brand
  // opens the composer from the table.
  useEffect(() => {
    if (!seedKey || !recipient) {
      seededKey.current = null;
      return;
    }

    const store = useAffiliateBulkEmailStore.getState();

    if (seededKey.current !== seedKey) {
      seededKey.current = seedKey;
      store.setRecipients([recipient]);
      store.setProgramId(programId);
      return;
    }

    // The address landed after the composer opened. Only the caller's own
    // recipient is refreshed: a colleague the brand added meanwhile, or a
    // recipient they removed, is theirs to keep.
    if (!store.recipients.has(recipient.id)) return;
    store.setRecipients(
      Array.from(store.recipients.values()).map((entry) =>
        entry.id === recipient.id ? recipient : entry
      )
    );
  }, [seedKey, recipient, programId]);

  const onClose = useCallback(() => {
    useAffiliateBulkEmailStore.getState().clearSelection();
    onCloseProp();
  }, [onCloseProp]);

  if (!request) return null;

  return (
    <BulkAffiliateEmailModal
      // A new request gets a new composer, so a draft never lands in a body
      // the brand started writing for someone else.
      key={request.seedKey}
      isOpen
      onClose={onClose}
      programId={request.programId ?? undefined}
      draft={request.draft}
    />
  );
};
